import { useLocation } from 'react-router-dom'
import { createContext, useEffect, useContext, useCallback, useState } from 'react'

declare global {
  interface Window {
    dataLayer: any[]
  }
}

// gtag-style push (the `arguments` object), only understood by GTM for `consent` commands
// eslint-disable-next-line prefer-rest-params
function pushGtagCommand(..._args: any[]): void {
  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments)
}

// GTM-style push: a plain object with an `event` key, which Custom Event triggers match on
function pushEvent(event: string, payload: Record<string, unknown> = {}): void {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event, ...payload })
}

type GtmDenyGranted = 'denied' | 'granted'
type Consent = 'accept_all' | 'analytics_only' | 'reject_all' | 'reset'
type ConsentChoice = 'unknown' | Consent

const CONSENT_STORAGE_KEY = 'gtm_consent'
const DEFAULT_CONSENT_DURATION_DAYS = 6 * 30 // 6 months
type ConsentStorageValue = { consent: Consent; expireAt: number | null }

type GtmContextType = {
  consent: ConsentChoice
  consentIsSet: boolean

  applyConsent: (consent: Consent) => void
  resetConsent: () => void
}

const GtmContext = createContext<GtmContextType | null>(null)

export const useGtmContext = (): GtmContextType => {
  const ctx = useContext(GtmContext)

  if (!ctx) {
    throw new Error('useGtmContext must be used within a GtmContextProvider')
  }

  return ctx
}

type GtmContextProviderProps = {
  consentDurationDays?: number
}

const GtmContextProvider: React.FC<React.PropsWithChildren<GtmContextProviderProps>> = ({
  consentDurationDays = DEFAULT_CONSENT_DURATION_DAYS,

  children,
}) => {
  const [consent, setConsent] = useState<ConsentChoice>(() => {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY)
    // Nothing stored yet
    if (!v) {
      return 'unknown'
    }

    try {
      const { consent, expireAt }: ConsentStorageValue = JSON.parse(v)

      switch (consent) {
        // Reject all is a definitive choice, it doesn't expire
        case 'reject_all':
          break
        // For other choices, if there's no expireAt or it's expired, we reset
        default:
          if (!expireAt || expireAt < Date.now()) {
            return 'unknown'
          }
          break
      }

      return consent
    } catch {
      return 'unknown'
    }
  })

  const applyConsent = useCallback(
    (consent: Consent) => {
      setConsent(consent)

      localStorage.setItem(
        CONSENT_STORAGE_KEY,
        JSON.stringify({
          consent,
          expireAt:
            consent !== 'reject_all'
              ? Date.now() + consentDurationDays * 24 * 60 * 60 * 1000
              : null,
        } satisfies ConsentStorageValue)
      )
    },
    [consentDurationDays]
  )

  const resetConsent = useCallback(() => {
    applyConsent('reset')
  }, [applyConsent])

  // Sync consent state with GTM dataLayer
  useEffect(() => {
    const setGtmConsent = ({
      analytics_storage = 'denied',
      ad_storage = 'denied',
      ad_user_data = 'denied',
      ad_personalization = 'denied',
    }: {
      analytics_storage?: GtmDenyGranted
      ad_storage?: GtmDenyGranted
      ad_user_data?: GtmDenyGranted
      ad_personalization?: GtmDenyGranted
    }) => {
      pushGtagCommand('consent', 'update', {
        analytics_storage,
        ad_storage,
        ad_user_data,
        ad_personalization,
      })
    }

    // `unknown` pushes nothing: the `consent default` command is sent by the inline
    // script in index.html, before gtm.js loads
    switch (consent) {
      case 'accept_all':
        setGtmConsent({
          analytics_storage: 'granted',
          ad_storage: 'granted',
          ad_user_data: 'granted',
          ad_personalization: 'granted',
        })
        break
      case 'analytics_only':
        setGtmConsent({ analytics_storage: 'granted' })
        break
      case 'reset':
      case 'reject_all':
        setGtmConsent({})
    }
  }, [consent])

  // Listen to route changes and push spa_page_view events
  const location = useLocation()

  useEffect(() => {
    // Skip page view tracking if consent is not accepted
    switch (consent) {
      case 'unknown':
      case 'reset':
      case 'reject_all':
        return
    }

    pushEvent('spa_page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    })
  }, [consent, location])

  return (
    <GtmContext.Provider
      value={{
        consent,
        consentIsSet: consent !== 'unknown' && consent !== 'reset',

        applyConsent,
        resetConsent,
      }}
    >
      {children}
    </GtmContext.Provider>
  )
}

export default GtmContextProvider
